import React from "react";
import { Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/context/LanguageContext";
import { MAX_CHARS } from "@/hooks/useLeadSubmission";

const Field = ({ id, label, error, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm font-medium text-[var(--brand-charcoal)]">{label}</Label>
    {children}
    {error && <p className="mt-1 text-xs text-[var(--danger)]" data-testid={`err-${id}`}>{error}</p>}
  </div>
);

const inputClass = (hasError) => `mt-1.5 h-11 rounded-xl ${hasError ? "border-[var(--danger)]" : ""}`;

export const LeadForm = ({ form, errors, submitting, onFieldChange, onSubmit }) => {
  const { t } = useLanguage();
  const remaining = MAX_CHARS - form.message.length;

  const handleSubmit = (ev) => {
    ev.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 sm:p-8" data-testid="lead-form" noValidate>
      <h3 className="font-heading text-xl font-semibold text-[var(--brand-navy)]">{t.portal.formTitle}</h3>

      <div className="mt-5 space-y-4">
        <Field id="name" label={t.portal.name} error={errors.name}>
          <Input id="name" value={form.name} onChange={(e) => onFieldChange("name", e.target.value)} className={inputClass(errors.name)} data-testid="lead-name" />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id="phone" label={t.portal.phone} error={errors.phone}>
            <Input id="phone" type="tel" value={form.phone} onChange={(e) => onFieldChange("phone", e.target.value)} className={inputClass(errors.phone)} data-testid="lead-phone" />
          </Field>
          <Field id="email" label={t.portal.email} error={errors.email}>
            <Input id="email" type="email" value={form.email} onChange={(e) => onFieldChange("email", e.target.value)} className={inputClass(errors.email)} data-testid="lead-email" />
          </Field>
        </div>
        <Field id="message" label={t.portal.message} error={errors.message}>
          <Textarea
            id="message"
            value={form.message}
            onChange={(e) => onFieldChange("message", e.target.value.slice(0, MAX_CHARS))}
            maxLength={MAX_CHARS}
            rows={4}
            className={`mt-1.5 resize-none rounded-xl ${errors.message ? "border-[var(--danger)]" : ""}`}
            data-testid="lead-message"
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-xs text-[var(--muted-ink)]">{t.portal.messageHelp}</p>
            <span className={`ml-3 shrink-0 text-xs font-medium ${remaining <= 50 ? "text-[var(--brand-burgundy)]" : "text-[var(--muted-ink)]"}`} aria-live="polite" data-testid="char-counter">
              {form.message.length}/{MAX_CHARS}
            </span>
          </div>
        </Field>
      </div>

      <Button type="submit" disabled={submitting} className="mt-6 h-12 w-full rounded-xl bg-[var(--brand-burgundy)] text-sm font-semibold text-white shadow-[var(--shadow-md)] transition-colors hover:bg-[var(--brand-burgundy-dark)] active:scale-[0.99] disabled:opacity-70" data-testid="lead-form-submit-button">
        {submitting ? t.portal.submitting : (<><Send className="mr-2 h-4 w-4" />{t.portal.submit}</>)}
      </Button>

      <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-[rgba(139,30,63,0.06)] px-4 py-3 text-center text-sm font-semibold text-[var(--brand-burgundy)]" data-testid="sla-banner">
        <Clock className="h-4 w-4" />
        {t.portal.slaBanner}
      </div>
    </form>
  );
};
